import { useEffect, useRef, useState } from "react"
import { cameraYaw } from "./turn"

// The phone's motion sensors, read while the camera is open: which way the
// back camera points (relative degrees, see turn.ts). No compass, no
// location, nothing leaves the phone. iPhones ask once ("Motion & Orientation
// Access"); the ask has to come from a tap, so primeMotion() runs in the tap
// that opens the camera. Refused or missing → no reading, and the walk just
// doesn't use it.

type OrientationCtor = typeof DeviceOrientationEvent & { requestPermission?: () => Promise<"granted" | "denied"> }

export function primeMotion(): void {
  const ctor = (typeof window !== "undefined" ? window.DeviceOrientationEvent : undefined) as OrientationCtor | undefined
  if (ctor?.requestPermission) void ctor.requestPermission().catch(() => {})
}

export interface CameraYaw {
  // Latest direction, or null (lying flat, no sensors yet).
  read: () => number | null
  // Updated a few times a second for drawing; null until sensors report.
  live: number | null
  // Bumps every time the sensors (re)start: readings from before don't
  // compare with readings after (each start may have its own zero).
  epoch: number
}

const DRAW_MS = 200

export function useCameraYaw(active: boolean): CameraYaw {
  const latest = useRef<number | null>(null)
  const [live, setLive] = useState<number | null>(null)
  const [epoch, setEpoch] = useState(0)

  useEffect(() => {
    if (!active || typeof window === "undefined") return
    latest.current = null
    setLive(null)
    setEpoch((e) => e + 1)
    let lastDraw = 0
    let pending: ReturnType<typeof setTimeout> | null = null
    const draw = () => {
      pending = null
      lastDraw = performance.now()
      setLive(latest.current)
    }
    const onOrientation = (e: DeviceOrientationEvent) => {
      const yaw = cameraYaw(e.alpha, e.beta, e.gamma)
      if (yaw === null) return
      latest.current = yaw
      // A few redraws a second, and always the latest reading at the end.
      if (pending) return
      const wait = DRAW_MS - (performance.now() - lastDraw)
      if (wait <= 0) draw()
      else pending = setTimeout(draw, wait)
    }
    window.addEventListener("deviceorientation", onOrientation)
    return () => {
      window.removeEventListener("deviceorientation", onOrientation)
      if (pending) clearTimeout(pending)
    }
  }, [active])

  return { read: () => latest.current, live, epoch }
}
